// A09b — Engineer application approval detail page. Reached from the
// Approval Queue (per-row "Review" button). Surfaces:
//   - Engineer profile snapshot (name, contact, tier, years, areas, bio)
//   - Photo ID image (Cap. 115 identity check — the whole point of the
//     approval gate, so it's rendered prominently)
//   - IT Certifications (each with the engineer's free-form label)
// Approve / Disapprove sit at the bottom and only fire after the reviewer
// has had a chance to see the documents above.

import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { tokens } from "@findgeeks/ui-tokens";
import { formatDateHK } from "@findgeeks/shared";
import { apiFetch } from "@/lib/api";
import { ConfirmModal } from "@/components/ConfirmModal";
import { useToast } from "@/components/Toast";
import { queryClient } from "@/lib/queryClient";

interface QualRow {
  id: string;
  kind: "photo_id" | "qualification";
  label: string | null;
  storage_path: string;
  reviewed_by: string | null;
  reviewed_at: string | null;
  expires_at: string | null;
  uploaded_at: string;
  signed_url: string | null;
}

interface DetailResponse {
  engineer: {
    user_id: string;
    qualification_level: string | null;
    approval_status: string;
    group_name: string | null;
    work_experience_years: number | null;
    bio: string | null;
    service_areas: string[] | null;
    rejection_reason: string | null;
    created_at: string;
    user_profiles: {
      id: string;
      display_name: string | null;
      first_name: string | null;
      last_name: string | null;
      email: string | null;
      phone: string | null;
      avatar_url: string | null;
      is_active: boolean;
    };
  };
  qualifications: QualRow[];
}

const QUAL_LABEL: Record<string, string> = {
  primary: "Primary Engineer",
  senior: "Senior Engineer",
  expert: "Expert Engineer",
};

export function ApprovalDetail() {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { show, ToastEl } = useToast();
  const [rejectOpen, setRejectOpen] = React.useState(false);
  const [reason, setReason] = React.useState("");

  const detail = useQuery<DetailResponse>({
    queryKey: ["engineer-application", id],
    queryFn: () => apiFetch(`/v1/admin/engineer-applications/${id}`),
    enabled: !!id,
  });

  const approve = useMutation({
    mutationFn: () =>
      apiFetch(`/v1/admin/engineer-applications/${id}/approve`, {
        method: "POST",
      }),
    onSuccess: () => {
      show("Application approved", "success");
      queryClient.invalidateQueries({ queryKey: ["engineer-applications"] });
      navigate("/app/app-users/approval");
    },
    onError: (err) =>
      show(err instanceof Error ? err.message : "Approve failed", "error"),
  });

  const reject = useMutation({
    mutationFn: () =>
      apiFetch(`/v1/admin/engineer-applications/${id}/reject`, {
        method: "POST",
        body: { reason },
      }),
    onSuccess: () => {
      show("Application disapproved", "success");
      queryClient.invalidateQueries({ queryKey: ["engineer-applications"] });
      setRejectOpen(false);
      navigate("/app/app-users/approval");
    },
    onError: (err) =>
      show(err instanceof Error ? err.message : "Disapprove failed", "error"),
  });

  if (detail.isLoading) {
    return (
      <Box sx={{ p: 3, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }
  if (detail.isError || !detail.data) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">Failed to load engineer detail.</Typography>
      </Box>
    );
  }

  const { engineer, qualifications } = detail.data;
  const fullName =
    [engineer.user_profiles.first_name, engineer.user_profiles.last_name]
      .filter(Boolean)
      .join(" ") ||
    engineer.user_profiles.display_name ||
    "—";
  const photoIds = qualifications.filter((q) => q.kind === "photo_id");
  const certs = qualifications.filter((q) => q.kind === "qualification");
  const isPending = engineer.approval_status === "pending";
  // Photo ID is the only hard requirement (Cap. 115). If it's missing the
  // API will 422 — disable the button so the admin can't trigger it.
  const canApprove = isPending && photoIds.length > 0;
  const canReject = isPending;

  const sectionHeading = {
    fontSize: tokens.font.sizes.body,
    fontWeight: tokens.font.weights.semibold,
    color: tokens.colors.admin.textHeading,
  } as const;
  const baseBtn = {
    px: 3,
    textTransform: "none" as const,
    fontWeight: tokens.font.weights.medium,
    color: "#fff",
    boxShadow: "none",
  };
  const approveBtn = {
    ...baseBtn,
    backgroundColor: "#5CB85C",
    "&:hover": { backgroundColor: "#4CAE4C", boxShadow: "none" },
  };
  const disapproveBtn = {
    ...baseBtn,
    backgroundColor: "#D9534F",
    "&:hover": { backgroundColor: "#C9302C", boxShadow: "none" },
  };

  return (
    <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2 }}>
      {/* Header — name + back link */}
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${tokens.colors.admin.border}`,
          borderRadius: 1,
          p: 3,
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Box>
            <Typography sx={sectionHeading}>{fullName}</Typography>
            <Typography
              sx={{ fontSize: 13, color: tokens.colors.admin.textMuted, mt: 0.5 }}
            >
              {engineer.user_profiles.email ?? "—"} ·{" "}
              {engineer.user_profiles.phone ?? "—"}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <Chip
                size="small"
                label={
                  QUAL_LABEL[engineer.qualification_level ?? ""] ?? "Engineer"
                }
              />
              <Chip
                size="small"
                color={
                  engineer.approval_status === "approved"
                    ? "success"
                    : engineer.approval_status === "rejected"
                      ? "error"
                      : "warning"
                }
                label={engineer.approval_status}
              />
              <Chip
                size="small"
                variant="outlined"
                label={`Submitted ${formatDateHK(engineer.created_at)}`}
              />
            </Stack>
          </Box>
          <Button
            size="small"
            variant="outlined"
            onClick={() => navigate("/app/app-users/approval")}
            sx={{ textTransform: "none" }}
          >
            ← Back to queue
          </Button>
        </Stack>
      </Paper>

      {/* Engineer profile fields */}
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${tokens.colors.admin.border}`,
          borderRadius: 1,
          p: 3,
        }}
      >
        <Typography sx={sectionHeading}>Engineer info</Typography>
        <Stack spacing={1.5} sx={{ mt: 2 }}>
          <Field
            label="Years of experience"
            value={
              engineer.work_experience_years != null
                ? String(engineer.work_experience_years)
                : "—"
            }
          />
          <Field
            label="Service areas"
            value={
              engineer.service_areas && engineer.service_areas.length > 0
                ? engineer.service_areas.join(", ")
                : "—"
            }
          />
          <Field label="Bio" value={engineer.bio || "—"} multiline />
          {engineer.rejection_reason ? (
            <Field
              label="Previous rejection reason"
              value={engineer.rejection_reason}
              multiline
            />
          ) : null}
        </Stack>
      </Paper>

      {/* Photo ID — the gating doc */}
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${tokens.colors.admin.border}`,
          borderRadius: 1,
          p: 3,
        }}
      >
        <Typography sx={sectionHeading}>
          Photo ID{" "}
          <Typography
            component="span"
            sx={{ fontSize: 12, color: tokens.colors.admin.textMuted, fontWeight: 400 }}
          >
            (Required — Cap. 115 identity check)
          </Typography>
        </Typography>
        {photoIds.length === 0 ? (
          <Typography sx={{ mt: 2, color: "#D9534F" }}>
            No photo ID uploaded.
          </Typography>
        ) : (
          <Stack spacing={1} sx={{ mt: 2 }}>
            {photoIds.map((q) => (
              <DocCard key={q.id} doc={q} />
            ))}
          </Stack>
        )}
      </Paper>

      {/* IT certifications — optional */}
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${tokens.colors.admin.border}`,
          borderRadius: 1,
          p: 3,
        }}
      >
        <Typography sx={sectionHeading}>
          IT Certifications{" "}
          <Typography
            component="span"
            sx={{ fontSize: 12, color: tokens.colors.admin.textMuted, fontWeight: 400 }}
          >
            (Optional — sets tier; engineers without certs default to Primary)
          </Typography>
        </Typography>
        {certs.length === 0 ? (
          <Typography sx={{ mt: 2, color: tokens.colors.admin.textMuted }}>
            No IT certifications uploaded.
          </Typography>
        ) : (
          <Stack spacing={1} sx={{ mt: 2 }}>
            {certs.map((q) => (
              <DocCard key={q.id} doc={q} />
            ))}
          </Stack>
        )}
      </Paper>

      {/* Action buttons */}
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${tokens.colors.admin.border}`,
          borderRadius: 1,
          p: 3,
        }}
      >
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button
            variant="contained"
            sx={disapproveBtn}
            disabled={!canReject || approve.isPending || reject.isPending}
            onClick={() => {
              setReason("");
              setRejectOpen(true);
            }}
          >
            Disapprove
          </Button>
          <Button
            variant="contained"
            sx={approveBtn}
            disabled={!canApprove || approve.isPending || reject.isPending}
            onClick={() => approve.mutate()}
          >
            Approve
          </Button>
        </Stack>
        {!isPending ? (
          <Typography
            sx={{ mt: 1.5, fontSize: 12, color: tokens.colors.admin.textMuted, textAlign: "right" }}
          >
            Application is already {engineer.approval_status}.
          </Typography>
        ) : isPending && photoIds.length === 0 ? (
          <Typography
            sx={{ mt: 1.5, fontSize: 12, color: "#D9534F", textAlign: "right" }}
          >
            Approve is disabled — engineer must upload a photo ID first.
          </Typography>
        ) : null}
      </Paper>

      <ConfirmModal
        open={rejectOpen}
        title="Disapprove application?"
        body={
          <Box>
            <Typography sx={{ mb: 1 }}>
              The engineer will be notified and asked to resubmit. Provide a
              clear reason — it appears verbatim in the engineer's app.
            </Typography>
            <TextField
              fullWidth
              required
              label="Reason"
              size="small"
              multiline
              minRows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </Box>
        }
        confirmLabel="Disapprove"
        danger
        pending={reject.isPending}
        onConfirm={() => {
          if (!reason.trim()) return;
          reject.mutate();
        }}
        onCancel={() => setRejectOpen(false)}
      />
      {ToastEl}
    </Box>
  );
}

function Field({
  label,
  value,
  multiline,
}: {
  label: string;
  value: string;
  multiline?: boolean;
}) {
  return (
    <Box>
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: tokens.font.weights.medium,
          color: tokens.colors.admin.textMuted,
          textTransform: "uppercase",
          letterSpacing: 0.5,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: 14,
          color: tokens.colors.admin.textBody,
          mt: 0.5,
          whiteSpace: multiline ? "pre-wrap" : "normal",
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function DocCard({ doc }: { doc: QualRow }) {
  return (
    <Box
      sx={{
        border: `1px solid ${tokens.colors.admin.border}`,
        borderRadius: 1,
        p: 1.5,
        display: "flex",
        gap: 2,
        alignItems: "flex-start",
      }}
    >
      {doc.signed_url ? (
        <Box
          component="a"
          href={doc.signed_url}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            display: "block",
            border: `1px solid ${tokens.colors.admin.border}`,
            borderRadius: 1,
            overflow: "hidden",
            flexShrink: 0,
          }}
        >
          <img
            src={doc.signed_url}
            alt={doc.label ?? doc.kind}
            style={{
              width: 160,
              height: 160,
              objectFit: "cover",
              display: "block",
            }}
          />
        </Box>
      ) : (
        <Box
          sx={{
            width: 160,
            height: 160,
            border: `1px dashed ${tokens.colors.admin.border}`,
            borderRadius: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: tokens.colors.admin.textMuted,
            fontSize: 12,
            flexShrink: 0,
          }}
        >
          (no preview)
        </Box>
      )}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: 14,
            fontWeight: tokens.font.weights.medium,
            color: tokens.colors.admin.textBody,
          }}
        >
          {doc.label ?? (doc.kind === "photo_id" ? "Photo ID" : "Qualification")}
        </Typography>
        <Typography
          sx={{ fontSize: 12, color: tokens.colors.admin.textMuted, mt: 0.5 }}
        >
          Uploaded {formatDateHK(doc.uploaded_at)}
        </Typography>
        {doc.reviewed_at ? (
          <Typography
            sx={{ fontSize: 12, color: "#5CB85C", mt: 0.25 }}
          >
            Reviewed {formatDateHK(doc.reviewed_at)}
          </Typography>
        ) : null}
        {doc.signed_url ? (
          <Typography
            component="a"
            href={doc.signed_url}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: "inline-block",
              mt: 0.5,
              fontSize: 12,
              color: tokens.colors.admin.primary,
              textDecoration: "none",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            Open full image ↗
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
}
